import React, { useState, useCallback, useEffect, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import CategoryIconSelection from "./categoryIcon.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import CategoryModal from "../../components/CustomComponents/modal/parentCategoryModel.jsx";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import moment from "moment";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule.js";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import { checkIfCommonTimeSlots, convertToScheduleFormat } from "../../components/CustomComponents/TimeSchedule/scheduleConv.js";
import CategoryDepartmentsModal from "../../components/CustomComponents/modal/rootCategoryGroup.jsx";
import ReportingCategoryModel from "../../components/CustomComponents/modal/reportingCategory";
import CategoryProductsSection from "./categoryProductsSection.jsx";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

function UpdateCategory() {
  const { rootCatID } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user).userData?.compID;
  const [categoryName, setCategoryName] = useState("");
  const [selectedParentCategory, setSelectedParentCategory] = useState({});
  const [selectedCategoryDepartment, setSelectedCategoryDepartment] = useState(null);
  const [selectedReportingCategory, setSelectedReportingCategory] = useState({});
  const [enableCategory, setEnableCategory] = useState(false);
  const [regularHours, setRegularHours] = useState(true);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showParentCategoryModal, setShowParentCategoryModal] = useState(false);
  const [showCategoryDepartmentsModal, setShowCategoryDepartmentsModal] = useState(false);
  const [showReportingCategoryModal, setShowReportingCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [reportingCategories, setReportingCategories] = useState([]);
  const [epos, setEpos] = useState(false);
  const [kiosk, setKiosk] = useState(false);
  const [online, setOnline] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [isAddOnCategory, setIsAddOnCategory] = useState(false);
  const [rootCatDetail, setRootCatDetail] = useState('');
  const [rootCatOrderBy, setRootCatOrderBy] = useState(0);
  const [categoryProductCount, setCategoryProductCount] = useState(0);
  const [schedules, setSchedules] = useState(schedule);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    {
      from: "00:00",
      to: "23:59",
    },
  ]);

  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      categoryName: categoryName || "",
      selectedParentCategoryId: selectedParentCategory?.rootCat_ID || 0,
      selectedCategoryDepartmentId: selectedCategoryDepartment?.cD_ID || null,
      selectedReportingCategoryId: selectedReportingCategory?.rC_ID || null,
      enableCategory: Boolean(enableCategory),
      regularHours: Boolean(regularHours),
      isAddOnCategory: Boolean(isAddOnCategory),
      selectedIcon: selectedIcon || "",
      epos: Boolean(epos),
      kiosk: Boolean(kiosk),
      online: Boolean(online),
      delivery: Boolean(delivery),
      rootCatDetail: rootCatDetail || "",
      rootCatOrderBy,
      useSameTimeSlots: Boolean(useSameTimeSlots),
      commonTimeSlot,
      schedules,
    });
  }, [
    categoryName,
    selectedParentCategory,
    selectedCategoryDepartment,
    selectedReportingCategory,
    enableCategory,
    regularHours,
    isAddOnCategory,
    selectedIcon,
    epos,
    kiosk,
    online,
    delivery,
    rootCatDetail,
    rootCatOrderBy,
    useSameTimeSlots,
    commonTimeSlot,
    schedules,
  ]);

  useEffect(() => {
    if (initialLoading) return;
    if (initialFormSnapshotRef.current !== null) return;
    // Defer so parent-department sync effect can settle after hydrate
    const timer = setTimeout(() => {
      if (initialFormSnapshotRef.current === null) {
        initialFormSnapshotRef.current = getFormSnapshot();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [initialLoading, getFormSnapshot]);

  const isDirty =
    !initialLoading &&
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();


  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  const handleUpdate = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!categoryName.trim()) {
      showErrorToast("Category name is required");
      return false;
    }
    if (!selectedCategoryDepartment) {
      showErrorToast("Category department is required");
      return false;
    }
    setLoading(true);

    const formattedSchedules = [];

    if (!regularHours) {
      if (useSameTimeSlots) {
        const days = [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ];
        days.forEach((day) => {
          if (commonTimeSlot.length > 0) {
            commonTimeSlot.forEach((slot) => {
              formattedSchedules.push({
                ID: 0,
                CompID: compID,
                rootCat_ID: parseInt(rootCatID),
                DayName: day,
                OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                UpdateDateTime: new Date().toISOString(),
              });
            });
          }
        });
      } else {
        schedules.forEach((schedule) => {
          if (schedule.checked && schedule.timeSlots.length > 0) {
            schedule.timeSlots.forEach((slot) => {
              if (
                moment(slot.from, ["h:mm A"]).isValid() &&
                moment(slot.to, ["h:mm A"]).isValid()
              ) {
                formattedSchedules.push({
                  ID: 0,
                  CompID: compID,
                  rootCat_ID: parseInt(rootCatID),
                  DayName: schedule.day,
                  OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                  CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                  UpdateDateTime: new Date().toISOString(),
                });
              }
            });
          }
        });
      }
    }

    const payload = {
      rootCat_ID: parseInt(rootCatID),
      localizeID: "en",
      compID,
      rootCat_Name: categoryName,
      rootCat_Detail: rootCatDetail,
      rootCat_Image: selectedIcon || "",
      rootCat_Publish: enableCategory,
      rootCat_Orderby: rootCatOrderBy,
      rG_ID: 1,
      rootCat_IsSubCategory: selectedParentCategory?.rootCat_ID ? true : false,
      rootCat_ParentRootCat_ID: selectedParentCategory?.rootCat_ID || 0,
      rootCat_ColorCode: "",
      pCategoryName: selectedParentCategory?.rootCat_Name || "",
      updateDateTime: new Date().toISOString(),
      isActiveforTill: epos,
      isActiveforKIOSK: kiosk,
      isActiveforTakeAway: online,
      isActiveforDelivery: delivery,
      isAvailableInTimeIntervals: !regularHours,
      isAddOnCategory: Boolean(isAddOnCategory),
      products: 0,
      subCategories: 0,
      subCategoriesList: [],
      rootCategoryWeeklyTimings: formattedSchedules,
      categoryDepartments: selectedCategoryDepartment ? [{
        cD_ID: selectedCategoryDepartment.cD_ID,
        cD_Name: selectedCategoryDepartment.cD_Name,
        compID: compID,
      }] : [],
      CategoryDepartmentRelations: selectedCategoryDepartment ? [{
        rootCat_ID: parseInt(rootCatID),
        cD_ID: selectedCategoryDepartment.cD_ID,
        compID: compID,
      }] : null,
      rootCat_Type: selectedReportingCategory?.rC_ID || 1, // Use rC_ID as RootCat_Type, default to 1 (Food)
    };
    console.log("Payload:", payload);

    try {
      const response = await axiosInstance.put(APIs.UPDATE_CATEGORY, payload);
      
      if (response.data.status) {
        showSuccessToast("Category updated successfully");
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          navigate('/categories/manageCategories');
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to update category");
        return false;
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Error updating category");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const applyReportingAndDepartmentFromDetails = useCallback(
    (categoryDetails, reportingCategoriesList) => {
      if (categoryDetails?.categoryDepartments?.[0]) {
        setSelectedCategoryDepartment(categoryDetails.categoryDepartments[0]);
      } else {
        setSelectedCategoryDepartment(null);
      }
      if (categoryDetails?.rootCat_Type && reportingCategoriesList?.length > 0) {
        const foundReportingCategory = reportingCategoriesList.find(
          (rc) => rc.rC_ID === categoryDetails.rootCat_Type
        );
        setSelectedReportingCategory(foundReportingCategory || {});
      } else {
        setSelectedReportingCategory({});
      }
    },
    []
  );

  const handleParentCategorySelection = (selectedCategory) => {
    if (
      selectedCategory?.id === "" ||
      selectedCategory?.id === "none" ||
      !selectedCategory?.rootCat_ID
    ) {
      setSelectedParentCategory({});
      setSelectedCategoryDepartment(null);
      setSelectedReportingCategory({});
    } else {
      setSelectedParentCategory(selectedCategory);
    }
  };

  const handleCategoryDepartmentsSelection = (selectedDepartment) => {
    setSelectedCategoryDepartment(selectedDepartment);
    setShowCategoryDepartmentsModal(false);
  };

  const handleReportingCategorySelection = (selectedCategory) => {
    setSelectedReportingCategory(selectedCategory);
    setShowReportingCategoryModal(false);
  };

  const fetchCategories = useCallback(async (options = {}) => {
    const showGlobalLoading = options.showGlobalLoading !== false;
    if (!compID) {
      return;
    }

    try {
      if (showGlobalLoading) setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          compID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });
   

      const categoryData = response.data.data || response.data;

      if (Array.isArray(categoryData)) {
        setCategories(categoryData.filter(category => category.rootCat_ID !== parseInt(rootCatID)));
      } else {
        setCategories([]);
      }
    } catch (err) {
      setCategories([]);
    } finally {
      if (showGlobalLoading) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  }, [compID, rootCatID]);

  const fetchReportingCategories = useCallback(async () => {
    if (!compID) {
      return [];
    }
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_TYPES, {
        params: {
          compID: compID,
        },
      });
      const categoryData = response.data.data || response.data;
      const reportingCategoriesData = Array.isArray(categoryData) ? categoryData : [];
      setReportingCategories(reportingCategoriesData);
      return reportingCategoriesData;
    } catch (err) {
      setReportingCategories([]);
      return [];
    }
  }, [compID]);

  const GetCategoryDetail = async (rootCatID) => {
    const response = await axiosInstance.get(APIs.GET_CATEGORY_ROOT_ID, {
      params: {
        compID,
        RootCat_ID: rootCatID,
      },
    });
    console.log("Category Detail Response:", response);
    const categoryDetails = response.data.data || response.data;
    return categoryDetails;
  };

  const fetchCategoryDetails = useCallback(async (reportingCategoriesList = []) => {
    if (!compID || !rootCatID) {
      return;
    }

    try {
      setLoading(true);
      const categoryDetails = await GetCategoryDetail(rootCatID);
  
      // Populate form fields with existing data
      setCategoryName(categoryDetails.rootCat_Name || "");
      if (categoryDetails.rootCat_ParentRootCat_ID > 0) {
        const parentCategory = await GetCategoryDetail(
          categoryDetails.rootCat_ParentRootCat_ID
        );
        setSelectedParentCategory(parentCategory);
        applyReportingAndDepartmentFromDetails(
          parentCategory,
          reportingCategoriesList
        );
      } else {
        setSelectedParentCategory({});
        applyReportingAndDepartmentFromDetails(
          categoryDetails,
          reportingCategoriesList
        );
      }
      setEnableCategory(categoryDetails.rootCat_Publish);
      setRootCatDetail(categoryDetails.rootCat_Detail);
      setRootCatOrderBy(categoryDetails.rootCat_Orderby);
      setRegularHours(!categoryDetails.isAvailableInTimeIntervals || false);
      // Normalize icon path - remove domain if present, ensure it starts with /
      const iconPath = categoryDetails.rootCat_Image 
        ? categoryDetails.rootCat_Image.replace(/^https?:\/\/[^\/]+/, '').replace(/^\/+/, '/')
        : null;
      setSelectedIcon(iconPath);
      setEpos(categoryDetails.isActiveforTill);
      setOnline(categoryDetails.isActiveforTakeAway);
      setKiosk(categoryDetails.isActiveforKIOSK);
      setDelivery(categoryDetails.isActiveforDelivery);
      setIsAddOnCategory(categoryDetails.isAddOnCategory || categoryDetails.IsAddOnCategory || false);

      if (categoryDetails.rootCategoryWeeklyTimings && Array.isArray(categoryDetails.rootCategoryWeeklyTimings) && categoryDetails.rootCategoryWeeklyTimings.length > 0) {
        setRegularHours(false);
        const isCommon = checkIfCommonTimeSlots(categoryDetails.rootCategoryWeeklyTimings);
        if (isCommon) {
          const firstDaySlots = categoryDetails.rootCategoryWeeklyTimings
            .filter(
              (day) => day.dayName === categoryDetails.rootCategoryWeeklyTimings[0].dayName
            )
            .map((day) => ({
              from: day.openTime,
              to: day.closeTime,
            }));
          setCommonTimeSlot(firstDaySlots);
        } else {
          setUseSameTimeSlots(false);
        }
        const convertedSchedules = convertToScheduleFormat(categoryDetails.rootCategoryWeeklyTimings);
        setSchedules(convertedSchedules);
      } else {
        setSchedules(schedule);
      }
      
    } catch (err) {
      showErrorToast("Failed to fetch category details");
    } finally {
      setLoading(false);
    }
  }, [compID, rootCatID, applyReportingAndDepartmentFromDetails]);

  useEffect(() => {
    const parentId = selectedParentCategory?.rootCat_ID;
    if (!parentId || !compID) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const parentDetails = await GetCategoryDetail(parentId);
        if (cancelled) {
          return;
        }
        applyReportingAndDepartmentFromDetails(
          parentDetails,
          reportingCategories
        );
      } catch {
        if (!cancelled) {
          showErrorToast("Failed to load parent category settings");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedParentCategory?.rootCat_ID,
    compID,
    reportingCategories,
    applyReportingAndDepartmentFromDetails,
  ]);

  const fieldsLockedFromParent = Boolean(selectedParentCategory?.rootCat_ID);

  useEffect(() => {
    const initializeData = async () => {
      // First fetch categories and reporting categories in parallel
      const [, reportingCategoriesData] = await Promise.all([
        fetchCategories({ showGlobalLoading: false }),
        fetchReportingCategories()
      ]);
      
      // Then fetch category details with the reporting categories data
      await fetchCategoryDetails(reportingCategoriesData);
      setInitialLoading(false);
    };
    
    initializeData();
  }, [fetchCategories, fetchReportingCategories, fetchCategoryDetails]);

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleUpdate({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/categories/manageCategories">Manage Categories</Link>
            </li>
            <li className="breadcrumb-item active">Update Category</li>
          </ol>
          <h1 className="page-header mb-0">Update Category</h1>
        </div>

        <SaveActions
          onCancel={() => navigate("/categories/manageCategories")}
          onSave={() => handleUpdate({ exitAfterSave: false })}
          onSaveAndExit={() => handleUpdate({ exitAfterSave: true })}
          saving={loading}
          disabled={initialLoading}
        />
      </div>
      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            {initialLoading ? (
              <UpdateShimmer />
            ) : (
            <div
              className="p-4 rounded-3 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                border: isDarkMode ? "1px solid #404040" : undefined,
                color: isDarkMode ? "#ffffff" : undefined,
              }}
            >
              {/* Top Section: Icon Selection and Toggle Switches */}
              <div className="row mb-4 align-items-center">
                <div className="col-md-3 text-center">
                  <CategoryIconSelection selectedIcon={selectedIcon} setSelectedIcon={setSelectedIcon} />
                </div>
                <div className="col-md-9">
                  <div className="row row-cols-2 g-3">
                    {[
                      { label: "Enable Category", field: "enableCategory", checked: enableCategory, toggle: () => setEnableCategory(!enableCategory) },
                      { label: "KIOSK", field: "kiosk", checked: kiosk, toggle: () => setKiosk(!kiosk) },
                      { label: "Online", field: "online", checked: online, toggle: () => setOnline(!online) },
                      // { label: "Delivery", field: "delivery", checked: delivery, toggle: () => setDelivery(!delivery) },
                      { label: "E POS", field: "epos", checked: epos, toggle: () => setEpos(!epos) },
                      { label: "Modifier Category", field: "isAddOnCategory", checked: isAddOnCategory, toggle: () => setIsAddOnCategory(!isAddOnCategory) },
                    ].map(({ label, field, checked, toggle }) => (
                      <div key={field} className="col">
                        <ToggleSwitch
                          isChecked={checked}
                          onToggle={toggle}
                          label={label}
                          name={field}
                          themeColor={themeColor}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Fields Section */}
              <div className="row g-4">
                <div className="col-md-6">
                  <FormInput
                    label="Category Name *"
                    value={categoryName}
                    onchange={(e) => setCategoryName(e.target.value)}
                    type="text"
                    placeHolder="Enter Category Name"
                    name="categoryName"
                    required
                  />
                </div>
                
                <div className="col-md-6">
                  <div className="position-relative">
                    <label 
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                    >
                      Parent Category
                    </label>
                    <div 
                      className="form-control d-flex align-items-center justify-content-between cursor-pointer"
                      style={{
                        minHeight: "48px",
                        cursor: "pointer",
                        borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                        backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                        color: isDarkMode ? '#e2e8f0' : '#495057'
                      }}
                      onClick={() => setShowParentCategoryModal(true)}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {selectedParentCategory?.rootCat_Name ? (
                          <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                            {selectedParentCategory.rootCat_Name}
                          </span>
                        ) : (
                          <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                            Click to select parent category
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {selectedParentCategory?.rootCat_Name && (
                          <button
                            className="btn btn-link p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedParentCategory({});
                              setSelectedCategoryDepartment(null);
                              setSelectedReportingCategory({});
                            }}
                            style={{ 
                              fontSize: "16px",
                              color: isDarkMode ? '#a0aec0' : '#6c757d'
                            }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                        <i 
                          className={`fa ${showParentCategoryModal ? "fa-chevron-up" : "fa-chevron-down"}`}
                          style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="position-relative">
                    <label 
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                    >
                      Reporting Category Type
                    </label>
                    <div 
                      className="form-control d-flex align-items-center justify-content-between"
                      style={{
                        minHeight: "48px",
                        cursor: fieldsLockedFromParent ? "not-allowed" : "pointer",
                        borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                        backgroundColor: fieldsLockedFromParent
                          ? (isDarkMode ? '#343a40' : '#f8f9fa')
                          : (isDarkMode ? '#2d3436' : '#ffffff'),
                        color: isDarkMode ? '#e2e8f0' : '#495057',
                        opacity: fieldsLockedFromParent ? 0.9 : 1,
                      }}
                      onClick={() => {
                        if (!fieldsLockedFromParent) {
                          setShowReportingCategoryModal(true);
                        }
                      }}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {selectedReportingCategory?.rC_Name ? (
                          <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                            {selectedReportingCategory.rC_Name}
                          </span>
                        ) : (
                          <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                            {fieldsLockedFromParent
                              ? "Inherited from parent category"
                              : "Click to select reporting category"}
                          </span>
                        )}
                      </div>
                      {!fieldsLockedFromParent && (
                        <div className="d-flex align-items-center gap-2">
                          {selectedReportingCategory?.rC_ID && (
                            <button
                            className="btn btn-link p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedReportingCategory({});
                            }}
                            style={{ 
                              fontSize: "16px",
                              color: isDarkMode ? '#a0aec0' : '#6c757d'
                            }}
                          >
                            <i className="fa fa-times"></i>
                            </button>
                          )}
                          <i 
                            className={`fa ${showReportingCategoryModal ? "fa-chevron-up" : "fa-chevron-down"}`}
                            style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                          ></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="position-relative">
                    <label 
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                    >
                      Category Department *
                    </label>
                    <div 
                      className={`form-control d-flex align-items-center justify-content-between ${!selectedCategoryDepartment && !fieldsLockedFromParent ? 'border-danger' : ''}`}
                      style={{
                        minHeight: "48px",
                        cursor: fieldsLockedFromParent ? "not-allowed" : "pointer",
                        borderColor: !selectedCategoryDepartment && !fieldsLockedFromParent ? '#dc3545' : (isDarkMode ? '#4a5568' : '#e9ecef'),
                        backgroundColor: fieldsLockedFromParent
                          ? (isDarkMode ? '#343a40' : '#f8f9fa')
                          : (isDarkMode ? '#2d3436' : '#ffffff'),
                        color: isDarkMode ? '#e2e8f0' : '#495057',
                        opacity: fieldsLockedFromParent ? 0.9 : 1,
                      }}
                      onClick={() => {
                        if (!fieldsLockedFromParent) {
                          setShowCategoryDepartmentsModal(true);
                        }
                      }}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {selectedCategoryDepartment ? (
                          <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                            {selectedCategoryDepartment.cD_Name}
                          </span>
                        ) : (
                          <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                            {fieldsLockedFromParent
                              ? "Inherited from parent category"
                              : "Click to select department"}
                          </span>
                        )}
                      </div>
                      {!fieldsLockedFromParent && (
                        <div className="d-flex align-items-center gap-2">
                          {selectedCategoryDepartment && (
                            <button
                              className="btn btn-link p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCategoryDepartment(null);
                              }}
                              style={{ 
                                fontSize: "16px",
                                color: isDarkMode ? '#a0aec0' : '#6c757d'
                              }}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          )}
                          <i 
                            className={`fa ${showCategoryDepartmentsModal ? "fa-chevron-up" : "fa-chevron-down"}`}
                            style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                          ></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Time Scheduling Section */}
              <div className="row g-4 mt-2">
                <div className="col-12">
                  <div
                    className="card p-3"
                    style={{
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <h5 className="card-title mb-3">Time Scheduling</h5>
                    <div className="row g-3 d-flex align-items-center p-4">
                      <div className="col-6">
                        <ToggleSwitch
                          isChecked={regularHours}
                          onToggle={() => setRegularHours(!regularHours)}
                          label="Available in regular Business Hours"
                          name="regularHours"
                          themeColor={themeColor}
                        />
                      </div>
                    </div>
                    
                    {!regularHours && (
                      <>
                        <div className="row g-3 d-flex align-items-center p-4">
                          <div className="col-6">
                            <ToggleSwitch
                              isChecked={useSameTimeSlots}
                              onToggle={() => {
                                setUseSameTimeSlots(!useSameTimeSlots);
                                if (!useSameTimeSlots) {
                                  applyCommonTimeSlotToAll();
                                }
                              }}
                              label="Use same time slots for all days"
                              name="useSameTimeSlots"
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                        <hr />

                        {useSameTimeSlots ? (
                          <CommonTimeSlotForAllWeek
                            setCommonTimeSlots={setCommonTimeSlot}
                            commonTimeSlots={commonTimeSlot}
                            isProfilePage={false}
                          />
                        ) : (
                          <ScheduleManager
                            schedules={schedules}
                            setSchedules={setSchedules}
                            isProfilePage={false}
                            isDarkMode={isDarkMode}
                            themeColor={themeColor}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}
            <CategoryModal
              showParentCategoryModal={showParentCategoryModal}
              setShowParentCategoryModal={setShowParentCategoryModal}
              showButton={true}
              setSelectedParentCategory={setSelectedParentCategory}
              selectedParentCategory={selectedParentCategory}
              onSubmit={handleParentCategorySelection}
              categories={categories}
              fetchCategories={fetchCategories}
            />
            {!fieldsLockedFromParent && (
              <CategoryDepartmentsModal
                showModal={showCategoryDepartmentsModal}
                setShowModal={setShowCategoryDepartmentsModal}
                onSubmit={handleCategoryDepartmentsSelection}
                selectedGroups={selectedCategoryDepartment}
                setSelectedGroups={setSelectedCategoryDepartment}
                subtitle="Select category department for this category"
              />
            )}
            {!fieldsLockedFromParent && (
              <ReportingCategoryModel
                isModalOpen={showReportingCategoryModal}
                setIsModalOpen={setShowReportingCategoryModal}
                onCategorySubmit={handleReportingCategorySelection}
                compID={compID}
                selectedCategory={selectedReportingCategory}
                categories={reportingCategories}
                showButton={true}
                onRefresh={fetchReportingCategories}
              />
            )}
          </PanelBody>
        </Panel>

        {!initialLoading && (
          <Panel className="m-3">
            <PanelHeader>Products ({categoryProductCount})</PanelHeader>
            <PanelBody>
              <div
                className="p-4 rounded-3 shadow-sm"
                style={{
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  border: isDarkMode ? "1px solid #404040" : undefined,
                  color: isDarkMode ? "#ffffff" : undefined,
                }}
              >
                <CategoryProductsSection
                  compID={compID}
                  rootCatID={rootCatID}
                  isDarkMode={isDarkMode}
                  themeColor={themeColor}
                  onCountChange={setCategoryProductCount}
                />
              </div>
            </PanelBody>
          </Panel>
        )}
      </div>
</GridSectionLoader>
    </div>
  );
}

export default UpdateCategory;